import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Tooltip,
  Stack,
  InputLabel,
  useMediaQuery,
} from "@mui/material";
import { useForm } from "react-hook-form";
import helpers from "@sistemas.mundo/helper-functions";
import CustomCheckBox from "@sistemas.mundo/custom-checkbox";
import CustomAutoComplete from "@sistemas.mundo/custom-autocomplete";
import CustomDatePicker from "@sistemas.mundo/custom-datepicker";
import CustomTimePicker from "@sistemas.mundo/custom-timepicker";
import CustomTextField from "@sistemas.mundo/custom-textfield";
import dinamicFormHelpers from "./helpers";
import dinamicFormValidations from "./validations";
import ClearIcon from "@mui/icons-material/Clear";
import SendIcon from "@mui/icons-material/Send";
import { Link } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { DropzoneArea } from "@sistemas.mundo/mp-mui-dropzone";

const env = process.env.REACT_APP_CURRENT_ENV;
const keyCaptCha = env == "dev" ? process.env.KEY_CAPTCHA_DEV : process.env.KEY_CAPTCHA_PRO;

/**
 *
 * @param {*} param0 { campos, onSubmit,submitButtonPosition = 'left || right || center', submitButtonColor = 'primary', submitButtonText = 'Enviar' }
 * @returns
 */
const DynamicForm = ({
  campos,
  onSubmit,
  formId = "customForm",
  resetCamposOnSubmit = true,
  submitButtonPosition = "right",
  submitButtonColor = "primary",
  clearBtnColor = "primary",
  tooltipClearFilters = "Deshacer datos ingresados",
  btnFilterText = "Deshacer",
  iconFilterBtn = <ClearIcon />,
  submitButtonText = "Guardar",
  tooltipSubmitBtn = "Guardar",
  iconSubmitBtn = <SendIcon />,
  fullButtonSubmitWidth = true,
  showClearForm = true,
  showSubmitBtn = true,
  getForValues = null,
  customSubmitButtonSize = { xs: 12, sm: 12, md: 12, lg: 12, xl: 12 },
  customClearButtonSize = { xs: 12, sm: 6, md: 3, lg: 2, xl: 2 },
  handleBackResetForm = null,
  link = false,
}) => {

  const isMobile = useMediaQuery("(max-width:600px)");
  const getDefaultValues = () => {
    const camposClonados = helpers.object.cloneObjectArray(campos);
    let valores = {};
    camposClonados.map((x) => {
      x.elements.map((element) => {
        if (element.type != "button") {
          if (element.type === "autocomplete") {
            valores[element.name] = element.value ?? null;
          } else if (element.type === "checkbox") {
            valores[element.name] =
              element.defaultChecked ?? element.value ?? false;
          } else if (element.type === "date") {
            valores[element.name] = helpers.date.isValidDate(
              helpers.date.formatDateString(element.value, "YYYY/MM/DD")
            )
              ? element.value
              : null;
          } else if (element.type === "file") {
            valores[element.name] = element.value ?? [];
          } else {
            valores[element.name] = element.value ?? "";
          }
        }
      });
    });
    return valores;
  };

  const defaultValues = useMemo(() => getDefaultValues(), []);
  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
    setError,
    setValue,
    getValues,
    clearErrors,
    watch,
    reset,
    formState: { isValid },
  } = useForm({
    shouldUnregister: false,
    defaultValues: defaultValues,
  });

  const [isResetedForm, setIsResetedForm] = useState(false);
  const [enterPressed, setEnterPressed] = useState(false);

  /**
   * Cada vez que haya un cambio un campo se manda de vuelta los datos actualizados del formulario
   */

  useEffect(() => {
    initFileTypes();
    // handleBackValues();
  }, [watch()]);

  useEffect(() => {
    initValues();
  }, [campos]);

  /**
   * Esta funcion permite inicializar los valores de los campos que son controlados
   */
  const initValues = () => {
    const camposClonados = helpers.object.cloneObjectArray(campos);
    camposClonados.map((x) => {
      x.elements.map((element) => {
        if (element.controlled) {
          if (
            element.type === "file" ||
            (element.type === "autocomplete" && element.multiple)
          ) {
            setValue(element.name, element.value ?? []);
          } else if (element.type === "autocomplete" && !element.multiple) {
            setValue(element.name, element.value ?? null);
          } else if (element.type === "date") {
            setValue(element.name, element.value ?? null);
          } else {
            setValue(element.name, element.value ?? "");
          }
        } else {
          if (element.type === "autocomplete") {
            if (element.opciones === null || !element.opciones.length) {
              setValue(element.name, element.multiple ? [] : null);
            } else {
              setValue(element.name, getValues(element.name) ?? element.value);
            }
          }
        }
      });
    });
  };

  const initFileTypes = () => {
    campos.forEach((item) => {
      item.elements.forEach((item2) => {
        if (
          item2.type.toLowerCase() === "file" &&
          !getValues(item2.name) &&
          getValues(item2.name) !== item2.value
        ) {
          setIsResetedForm(true);
          setValue(item2.name, item2.value ?? []);
        }
      });
    });
  };

  const hasCustomValidation = (value) => {
    if (value.customValidation) {
      const resultValidation = value.customValidation(getValues(value.name));
      if (!resultValidation.isValid) {
        setError(value.name, {
          type: "invalid",
          message: resultValidation.message,
        });
        return false;
      }
      if (resultValidation.value) {
        setValue(value.name, resultValidation.value);
      }
    }
    return true;
  };

  /**
   * En este metodo se hacen las llamadas a todos los metodos adicionales de validación para los campos
   * Cada metodo de validacion debe retornar true en caso de que la validación sea correcta y
   * false en caso contrario
   * @param {*} value
   * @returns Retorna una promesa, Si la respuesta es true el formulario no contiene errores, false en caso contrario
   */
  const tieneValidacionAdicional = async (value = null) => {
    return new Promise((resolve) => {
      const {
        validarDependenciaOtroCampo,
        validarMayorQue,
        menorQue,
        validarTipoCampo,
        validateRequiredUploadFile,
      } = dinamicFormValidations;
      if (value) {
        resolve(
          validarDependenciaOtroCampo(value, getValues, setError) &&
            validarMayorQue(value, getValues, setError) &&
            menorQue(value, getValues, setError) &&
            validarTipoCampo(
              value,
              getValues,
              setError,
              setValue,
              clearErrors
            ) &&
            hasCustomValidation(value)
        );
      } else {
        let result = true;
        campos.map(async (x) => {
          x.elements.map(async (element) => {
            const resultado =
              validateRequiredUploadFile(
                element,
                getValues,
                setError,
                clearErrors
              ) &&
              hasCustomValidation(element) &&
              validarDependenciaOtroCampo(element, getValues, setError) &&
              validarMayorQue(element, getValues, setError) &&
              menorQue(element, getValues, setError) &&
              validarTipoCampo(
                element,
                getValues,
                setError,
                setValue,
                clearErrors
              );
            if (resultado) {
              clearErrors(element.name);
            }
            result = result && resultado;
          });
        });
        resolve(result);
      }
    });
  };

  /**
   * Se ejecuta cuando se envia el formulario
   * @param {*} form
   */
  const onClickEnviarForm = (form = null) => {
    if (enterPressed) {
      return;
    }
    if (form) {
      const camposClonados = helpers.object.cloneObjectArray(campos);
      camposClonados.map((x) => {
        x.elements.map((element) => {
          if (element.type === "date") {
            if (form[element.name] && !(form[element.name] instanceof Date)) {
              form[element.name] = new Date(
                helpers.date.formatDateString(form[element.name], "YYYY-MM-DD")
              );
              form[element.name] = form[element.name]
                .toISOString()
                .split("T")[0];
            } else {
              if (
                form[element.name] &&
                form[element.name].toString().includes("T")
              ) {
                form[element.name] = form[element.name]
                  .toISOString()
                  .split("T")[0];
              }
            }
          }
        });
      });
    }
    tieneValidacionAdicional().then((isValidForm) => {
      if (isValidForm && helpers.object.isEmpty(errors)) {
        if (form) {
          //&& isValid
          onSubmit(form);
        }
        resetFormularioOnSubmit();
      }
    });
  };

  /**
   * Retorna los valores del formulario cuando haya un cambio en cualquier campo
   */
  const handleBackValues = () => {
    if (getForValues) {
      const nuevosValores = helpers.object.cloneObjectArray(getValues());
      const datosIniciales = helpers.object.cloneObjectArray(defaultValues);
      getForValues(nuevosValores, datosIniciales);
    }
  };

  /**
   * Metodo que retorna el action onChange de un campo al component principal
   * @param {*} value
   * @param {*} campo
   */
  const handleOnChange = (value, campo, notifBackValues = false) => {
    if (campo.onChange) {
      campo.onChange(value);
    }
    if (notifBackValues) {
      handleBackValues();
    }
  };

  /**
   * Metodo que retorna el action onBlur de un campo al component principal
   * @param {*} value
   * @param {*} campo
   */
  const handleOnBlur = (value, campo) => {
    if (campo.onBlur) {
      campo.onBlur(value);
    }
    handleBackValues();
  };

  /**
   * Retorna la acción onKeyDown del campo al componente principal
   * @param {*} value
   * @param {*} campo
   */
  const handleKeyDown = (value, campo) => {
    if (campo.onKeyDown) {
      campo.onKeyDown(value);
    }
  };

  const onChangeUploadFile = (value, campo) => {
    const { validateRequiredUploadFile } = dinamicFormValidations;
    if (value && Array.isArray(value)) {
      setValue(campo.name, value);
      handleOnChange(value, campo);
    } else {
      setValue(campo.name, []);
      handleOnChange([], campo);
    }
    validateRequiredUploadFile(campo, getValues, setError, clearErrors);
  };

  const onChangeCampo = (value, campo) => {
    if (campo.customFormat) {
      const result = campo.customFormat(value);
      setValue(campo.name, result ?? value);
    } else {
      setValue(campo.name, value);
    }
    handleOnChange(value, campo);
  };

  const onBlurCampo = (value, campo) => {
    if (value) setValue(campo.name, value.trim());
    const { validarTipoCampo } = dinamicFormValidations;
    const resultValidateRut = validarTipoCampo(
      campo,
      getValues,
      setError,
      setValue,
      clearErrors
    );
    if (resultValidateRut) {
      handleOnBlur(value, campo);
    }
  };

  const onChangeSelect = (value, campo) => {
    setValue(campo.name, value);
    handleOnChange(value, campo, true);
  };

  const onChangeCheckBox = (value, campo) => {
    setValue(campo.name, value);
    if (value && errors[campo.name]?.type === "required") {
      clearErrors(campo.name);
    }
    handleOnChange(value, campo, true);
  };

  const onChangeDatePicker = (value, campo) => {
    if (
      helpers.date.isValidDate(
        helpers.date.formatDateString(value, "YYYY/MM/DD")
      ) ||
      value === null
    ) {
      let { error, message } = validateRangoFecha(value, campo);
      if (error) {
        setError(campo.name, {
          type: "invalid",
          message: message,
        });
      } else {
        setValue(campo.name, value);
        clearErrors(campo.name);
      }
    } else {
      setError(campo.name, {
        type: "invalid",
        message: "Fecha inválida",
      });
    }
    handleOnChange(value, campo, true);
  };

  const validateRangoFecha = (value, campo) => {
    let minFecha = new Date("1900/01/01");
    let maxFecha = new Date("2100/01/01");
    if (value < minFecha) {
      return { error: true, message: "Fecha bajo el rango mínimo" };
    }
    if (value > maxFecha) {
      return { error: true, message: "Fecha sobre el rango máximo" };
    }
    return { error: false, message: "" };
  };

  const onChangeTimePicker = (value, campo) => {
    setValue(campo.name, value);
    handleOnChange(value, campo, true);
  };

  /**
   * Retorna el mensaje correspondiente dependiendo del tipo de campo
   * @param {*} x
   * @returns
   */

  function bytesToMegabytes(bytes) {
    return Math.floor(bytes / (1024 * 1024));
  }
  const hasError = (x) => {
    const result = { value: true, message: "" };
    if (errors[x.name]) {
      switch (errors[x.name].type) {
        case "invalid":
          result.message = errors[x.name].message;
          break;
        case "pattern":
          result.message = errors[x.name].message;
          break;
        case "dependency":
          result.message = errors[x.name].message;
          break;
        case "required":
          result.message =
            x.requiredMessage ??
            helpers.formErrorMessages.requiredMessage(x.label);
          break;
        case "maxLength":
          result.message =
            x.elementMessage ??
            helpers.formErrorMessages.maxLength(x.type, x.maxLength);
          break;
        case "minLength":
          result.message =
            x.minLengthMessage ??
            helpers.formErrorMessages.minLength(x.type, x.minLength);
          break;
        default:
          result.value = false;
          break;
      }
    }

    if (!result.message) result.value = false;

    return result;
  };

  const getElements = (x) => {
    return x.elements.map((element, key) => {
      const { xs, md, lg, xl } = dinamicFormHelpers.getGridSize(element);
      const { pl, pr, pb, pt } = dinamicFormHelpers.getPadding(
        element,
        element.type
      );
      const visible = typeof element.visible === "undefined" || element.visible;
      const currentError = hasError(element);

      switch (element.type) {
        case "text":
        case "rut":
        case "phone":
        case "number":
        case "password":
        case "email":
        case "textarea":
          return (
            ((visible && !element.showIf) ||
              (element.showIf &&
                element.showIf.name &&
                watch(element.showIf.name) &&
                dinamicFormValidations.showIf(element, getValues))) && (
              <Grid
                item
                key={key}
                style={{ textAlign: element.position ?? ""}}
                xs={xs}
                md={md}
                lg={lg}
                xl={xl}
            
              >
                <Box /* pl={pl} pr={pr} */ pb={pb} pt={pt} style={{marginLeft:10, marginRigth:10}} >
                  {element.label && (
                    <div style={{ display: "flex" }}>
                      <p style={{ fontSize: "12px", fontWeight: 500 }}>
                        {element.label}{" "}
                      </p>
                      {/* {element.required && (
                        <p
                          style={{
                            color: "red",
                            fontSize: "12px",
                            fontWeight: 500,
                          }}
                        >
                          *
                        </p>
                      )} */}
                    </div>
                  )}
                 
                  <CustomTextField
                    size={element.size ?? "medium"}
                    type={
                      element.type === "password" || element.type === "textarea"
                        ? element.type
                        : "text"
                    }
                    register={register}
                    required={element.required ?? false}
                    name={element.name}
                    label={
                      element.required ? `${element.label} *` : element.label
                    }
                    placeholder={element.placeholder ?? ""}
                    value={getValues(element.name)}
                    hashError={currentError}
                    autoComplete={element.autoComplete ?? "off"}
                    rows={
                      element.type === "textarea" ? element.rows ?? 4 : null
                    }
                    multiline={element.type === "textarea" ? true : false}

                    maxLength={element.maxlength ?? null}
                    minLength={element.minLength ?? 0}
                    pattern={element.pattern ?? null}
                    onChange={(value) => onChangeCampo(value, element)}
                    onBlur={(value) => onBlurCampo(value, element)}
                    onKeyDown={(value) => handleKeyDown(value, element)}
                    errorMessage={element.errorMessage ?? null}
                    disabled={element.disabled ?? false}
                    icon={element.icon ?? false}
                  />
                </Box>
              </Grid>
            )
          );
        case "autocomplete":
          return (
            ((visible && !element.showIf) ||
              (element.showIf &&
                element.showIf.name &&
                watch(element.showIf.name) &&
                dinamicFormValidations.showIf(element, getValues))) && (
              <Grid
                item
                key={key}
                style={{ textAlign: element.position ?? "" }}
                xs={xs}
                md={md}
                lg={lg}
                xl={xl}
              >
                <Box pl={pl} pr={pr} pb={pb} pt={pt}>
                  <CustomAutoComplete
                    size={element.size ?? "medium"}
                    multiple={element.multiple ?? false}
                    required={element.required ?? false}
                    disabled={element.disabled ?? false}
                    name={element.name}
                    lazy={element.lazy ?? false}
                    label={
                      element.required ? `${element.label} *` : element.label
                    }
                    control={control}
                    opciones={element.opciones}
                    value={watch(element.name) && getValues(element.name)}
                    hashError={currentError}
                    onChange={(value) => onChangeSelect(value, element)}
                    clearErrors={clearErrors}
                    placeholder={element.placeholder ?? ""}
                    style={{ fontFamily: "Montserrat" }}
                  />
                </Box>
              </Grid>
            )
          );
        case "date":
          return (
            ((visible && !element.showIf) ||
              (element?.showIf &&
                element?.showIf.name &&
                watch(element?.showIf?.name) &&
                dinamicFormValidations.showIf(element, getValues))) && (
              <Grid
                item
                key={key}
                style={{ textAlign: element.position ?? "" }}
                xs={xs}
                md={md}
                lg={lg}
                xl={xl}
              >
                <Box pl={pl} pr={pr} pb={pb} pt={pt}>
                  <CustomDatePicker
                    size={element.size ?? "medium"}
                    control={control}
                    register={register}
                    hashError={currentError}
                    name={element.name}
                    label={
                      element.required ? `${element.label} *` : element.label
                    }
                    value={watch(element.name) && getValues(element.name)}
                    cancelText={element.cancelText ?? "Cancelar"}
                    clearText={element.clearText ?? "Limpiar"}
                    inputFormat={element?.inputFormat}
                    returnFormat={element?.returnFormat}
                    leftArrowButtonText={
                      element.leftArrowButtonText ?? "Anterior"
                    }
                    rightArrowButtonText={
                      element.rightArrowButtonText ?? "Siguiente"
                    }
                    clearable={element.clearable ?? false}
                    disabled={element.disabled ?? false}
                    required={element.required ?? false}
                    showToolbar={element.showToolbar ?? false}
                    orientation={element.orientation ?? "portrait"}
                    onOpen={element.onOpen ?? null}
                    onClose={element.onClose ?? null}
                    onAccept={element.onAccept ?? null}
                    onYearChange={element.onYearChange ?? null}
                    onBlur={element.onBlur ?? null}
                    onChange={(value) => onChangeDatePicker(value, element)}
                    loading={element.loading ?? false}
                  />
                </Box>
              </Grid>
            )
          );
        case "recaptcha":
          return (
            ((visible && !element.showIf) ||
              (element?.showIf &&
                element?.showIf.name &&
                watch(element?.showIf?.name) &&
                dinamicFormValidations.showIf(element, getValues))) && (
              <Grid
                item
                key={key}
                style={{ textAlign: element.position ?? "" }}
                sx={{ display: "grid", placeItems: "center" }}
                xs={xs}
                md={md}
                lg={lg}
                xl={xl}
                {...register(element.name, {
                  required: !element.token,
                })}
              >
                <Box pl={pl} pr={pr} pb={pb} pt={pt}>
                  <ReCAPTCHA
                    sitekey={keyCaptCha}
                    onChange={element.onSuccesscaptcha}
                    onExpired={element.onExpiredcaptcha}
                    size={isMobile ? "compact" : "normal"}
                  />

                  {currentError.value && !element.token && (
                    <p
                      style={{
                        fontSize: "0.75rem",
                        color: "#d32f2f",
                        fontWeight: 400,
                        fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
                      }}
                    >
                      {currentError.message}
                    </p>
                  )}
                </Box>
              </Grid>
            )
          );
        case "time":
          return (
            ((visible && !element.showIf) ||
              (watch(element.showIf.name) &&
                dinamicFormValidations.showIf(element, getValues))) && (
              <Grid
                item
                key={key}
                style={{ textAlign: element.position ?? "" }}
                xs={xs}
                md={md}
                lg={lg}
                xl={xl}
              >
                <Box pl={pl} pr={pr} pb={pb} pt={pt}>
                  <CustomTimePicker
                    size={element.size ?? "medium"}
                    register={register}
                    name={element.name}
                    label={
                      element.required ? `${element.label} *` : element.label
                    }
                    value={watch(element.name) && getValues(element.name)}
                    cancelText={element.cancelText ?? "Cancelar"}
                    clearText={element.clearText ?? "Limpiar"}
                    inputFormat={element?.inputFormat}
                    returnFormat={element?.returnFormat}
                    leftArrowButtonText={
                      element.leftArrowButtonText ?? "Anterior"
                    }
                    rightArrowButtonText={
                      element.rightArrowButtonText ?? "Siguiente"
                    }
                    clearable={element.clearable ?? false}
                    disabled={element.disabled ?? false}
                    required={element.required ?? false}
                    showToolbar={element.showToolbar ?? false}
                    orientation={element.orientation ?? "portrait"}
                    onOpen={element.onOpen ?? null}
                    onClose={element.onClose ?? null}
                    onAccept={element.onAccept ?? null}
                    onYearChange={element.onYearChange ?? null}
                    onBlur={element.onBlur ?? null}
                    loading={element.loading ?? false}
                    hashError={currentError}
                    onChange={(value) => onChangeTimePicker(value, element)}
                  />
                </Box>
              </Grid>
            )
          );
        case "checkbox":
          return (
            ((visible && !element.showIf) ||
              (element?.showIf &&
                element?.showIf.name &&
                watch(element?.showIf?.name) &&
                dinamicFormValidations.showIf(element, getValues))) && (
              <Grid
                item
                key={key}
                style={{ textAlign: element.position ?? "" }}
                xs={xs}
                md={md}
                lg={lg}
                xl={xl}
              >
                <Box pl={pl} pr={pr} pb={pb} pt={pt}>
                  <CustomCheckBox
                    size={element.size ?? "medium"}
                    control={control}
                    name={element.name}
                    label={
                      element.required ? `${element.label} *` : element.label
                    }
                    descripcion={
                      element.descripcion ? element.descripcion : "Si"
                    }
                    value={
                      watch(element.name) && getValues(element.name)
                        ? {
                            currentValue: element.value,
                            newValue: getValues(element.name),
                          }
                        : null
                    }
                    onChange={(value) => onChangeCheckBox(value, element)}
                    hashError={currentError}
                    required={element.required ? element.required : false}
                    disabled={element.disabled ? element.disabled : false}
                  />
                </Box>
              </Grid>
            )
          );
        case "file":
          const x = bytesToMegabytes(element.maxFileSize ?? 5000000);
          return (
            <Grid
              item
              key={key}
              style={{ textAlign: element.position ?? "" }}
              xs={xs}
              md={md}
              lg={lg}
              xl={xl}
            >
              <DropzoneArea
                dropzoneText={
                  /* element.label ?? */ "Arrastrar archivos o hacer clic aquí"
                }
                filesLimit={element.filesLimit ?? 1}
                name={element.name}
                acceptedFiles={element.acceptedFiles ?? []}
                maxFileSize={element.maxFileSize ?? 5000000}
                reset={isResetedForm}
                onReset={() => setIsResetedForm(false)}
                required={element.required ?? false}
                onChange={(file) => onChangeUploadFile(file, element)}
                showPreviews={element.showPreviews ?? false}
                showPreviewsInDropzone={element.showPreviewsInDropzone ?? true}
                useChipsForPreview={element.useChipsForPreview ?? true}
                previewGridProps={{
                  container: { spacing: 1, direction: "row" },
                }}
                showAlerts={["error"]}
              />
              {errors[element.name] && (
                <InputLabel color="error" error={true}>
                  {errors[element.name].message}
                </InputLabel>
              )}
              {/* error typeo: felx*/}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <p style={{ fontSize: "12px", opacity: 0.8 }}>
                  {`Las extensiones permitidas son: ${
                    element.labelFiles ?? ""
                  } Carga los archivos que desees (máx. ${
                    element.filesLimit ?? 1
                  }
                  archivos / ${x}mb de tamaño).`}
                </p>
              </div>
            </Grid>
          );
        case "button":
          return (
            ((visible && !element.showIf) ||
              (element.showIf &&
                element.showIf.name &&
                watch(element.showIf.name) &&
                dinamicFormValidations.showIf(element, getValues))) && (
              <Grid
                item
                key={key}
                style={{ textAlign: element.position ?? "" }}
                xs={xs}
                md={md}
                lg={lg}
                xl={xl}
              >
                <Box pl={pl} pr={pr} pb={pb} pt={pt}>
                  <Button
                    id={element.id ?? element.name}
                    name={element.name}
                    variant={element.variant ?? "contained"}
                    fullWidth={element.fullWidth ?? true}
                    onClick={element?.onClick ?? null}
                    color={element?.color ?? submitButtonColor}
                    startIcon={element?.icon ?? null}
                  >
                    {element.label}
                  </Button>
                </Box>
              </Grid>
            )
          );
        case "custom":
          return (
            ((visible && !element.showIf) ||
              (element.showIf &&
                element.showIf.name &&
                watch(element.showIf.name) &&
                dinamicFormValidations.showIf(element, getValues))) && (
              <Grid
                item
                key={key}
                style={{ textAlign: element.position ?? "", display:"grid",paddingTop:'8px'  }}
                xs={xs}
                md={md}
                lg={lg}
                xl={xl}
             
              >
                <Box pl={pl} pr={pr} pb={pb} pt={pt} style={{ paddingTop:'0px'  }}>
                  {element.component}
                </Box>
              </Grid>
            )
          );
        default:
          break;
      }
    });
  };

  const resetFormularioOnSubmit = () => {
    if (resetCamposOnSubmit && isValid) {
      resetFormulario();
    }
  };

  const resetFormulario = () => {
    setIsResetedForm(true);
    reset(getDefaultValues());
    clearErrors();
    if (handleBackResetForm) {
      handleBackResetForm(true);
    }
  };

  const checkKeyDown = (e) => {
    if (e.keyCode === 13) {
      setEnterPressed(true);
    }
  };
  const checkKeyUp = (e) => {
    if (e.keyCode === 13) {
      setEnterPressed(false);
    }
    if (e.keyCode === 32) {
      e.preventDefault();
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onClickEnviarForm)}
      onKeyDown={(e) => checkKeyDown(e)}
      onKeyUp={(e) => checkKeyUp(e)}
      autoComplete="new-password"
      id={formId}
    >
      {campos.map((x, key) => {
        return (
          <Grid container key={key}>
            <Grid item xs={12}>
              {x.title}
            </Grid>
            {getElements(x)}
          </Grid>
        );
      })}
      {/* {link && (
        <Grid container>
          <Grid
            item
            xs={12}
            sx={{ display: "grid", placeItems: "end", marginBottom: "2rem" }}
          >
            <a
              onClick={() => link(true)}
              style={{
                color: "#00657d",
                fontSize: "13px",
                textAlign: "star",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              Cambiar correo/contraseña
            </a>
          </Grid>
        </Grid>
      )} */}

      <Box pt={2}>
        <Grid
          justifyContent={submitButtonPosition}
          //alignItems="center"
          //sx={{ display: "grid", placeItems: "center" }}
        >
          {showSubmitBtn && (
            <Grid
              item
              xs={customSubmitButtonSize.xs}
              sm={customSubmitButtonSize.sm}
              md={customSubmitButtonSize.md}
              lg={customSubmitButtonSize.lg}
              xl={customSubmitButtonSize.xl}
              //sx={{backgroundColor:"red", width:"100%"}}
            >
              
              <Tooltip title={tooltipSubmitBtn}>
                <Button
                  id={formId + "_btnEnviar"}
                  variant="contained"
                  fullWidth={fullButtonSubmitWidth}
                  type="submit"
                  color={submitButtonColor}
                  sx={{
                    fontFamily: "Montserrat",
                    fontSize: "13px",
                    paddingX: "3rem",
                    background: "#00657d 0% 0% no-repeat padding-box",
                    borderRadius: "23px",
                    opacity: 1,
                    ":hover": { background: "#00657d" },
                  }}
                  
                >
                  <Fragment>
                    {submitButtonText}
                    {/* {iconSubmitBtn ? <span>&nbsp;</span> : ""} {iconSubmitBtn} */}
                  </Fragment>
                </Button>
              </Tooltip>
            </Grid>
          )}
          {showClearForm && (
            <Grid
              item
              xs={customClearButtonSize.xs}
              sm={customClearButtonSize.sm}
              md={customClearButtonSize.md}
              lg={customClearButtonSize.lg}
              xl={customClearButtonSize.xl}
            >
              <Tooltip title={tooltipClearFilters}>
                <Button
                  id={formId + "_btnClearFilters"}
                  variant="contained"
                  fullWidth={fullButtonSubmitWidth}
                  color={clearBtnColor}
                  onClick={resetFormulario}
                >
                  <Fragment>
                    {btnFilterText}
                    {iconFilterBtn ? <span>&nbsp;</span> : ""} {iconFilterBtn}
                  </Fragment>
                </Button>
              </Tooltip>
            </Grid>
          )}
        </Grid>
      </Box>
    </form>
  );
};

export default DynamicForm;
